import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Card,
  CardContent,
  TablePagination,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import PersonIcon from '@mui/icons-material/Person';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as XLSX from 'xlsx';
import { toast } from 'react-hot-toast';
import axios from '../../utils/axios';
import Layout from '../../components/layout/Layout';
import salesmanService from '../../services/salesmanService';
import authService from '../../services/authService';
import { format } from 'date-fns';
import { formatCurrency } from '../../utils/formatCurrency';
import { formatDate, formatDateTime } from '../../utils/dateFormatter';
import ReportMetricCard from '../../components/reports/ReportMetricCard';
import { reportPageSx } from '../../components/reports/reportPageLayout';

const SalesmanReportPage = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [salesmen, setSalesmen] = useState([]);
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [toDate, setToDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [salesmanId, setSalesmanId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [userLocation, setUserLocation] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(10);
  const [isExporting, setIsExporting] = useState(false);

  const initializeUserContext = useCallback(async () => {
    try {
      const response = await axios.get('/auth/me');
      const userData = response.data || {};
      const role = userData.role || authService.getUserRole();
      setIsAdmin(role === 'ADMIN');
      setUserLocation(userData.location || null);
    } catch (e) {
      setIsAdmin(authService.getUserRole() === 'ADMIN');
      setUserLocation(null);
    } finally {
      setFiltersReady(true);
    }
  }, []);

  const fetchSalesmen = useCallback(async () => {
    try {
      if (isAdmin) {
        const list = await salesmanService.list();
        setSalesmen(Array.isArray(list) ? list : []);
      } else if (userLocation?.id) {
        const list = await salesmanService.list({ locationId: userLocation.id });
        setSalesmen(Array.isArray(list) ? list : []);
      } else {
        setSalesmen([]);
      }
    } catch (e) {
      setSalesmen([]);
    }
  }, [isAdmin, userLocation]);

  useEffect(() => {
    initializeUserContext();
  }, [initializeUserContext]);

  useEffect(() => {
    if (filtersReady) {
      fetchSalesmen();
    }
  }, [filtersReady, fetchSalesmen]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ fromDate, toDate });
      if (salesmanId) params.set('salesmanId', salesmanId);
      if (invoiceNo && invoiceNo.trim()) params.set('orderNumber', invoiceNo.trim());
      if (!isAdmin && userLocation?.id) params.set('locationId', userLocation.id);
      const res = await axios.get(`/reports/salesman?${params.toString()}`);
      setRows(Array.isArray(res.data) ? res.data : []);
      setPage(0); // Reset to first page on new fetch
    } catch (e) {
      console.error('Salesman report error', e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate, salesmanId, invoiceNo, isAdmin, userLocation]);

  useEffect(() => {
    if (filtersReady) {
      fetchReport();
    }
  }, [filtersReady, fetchReport]);

  const totalBills = rows.length;
  const totalAmount = rows.reduce((sum, r) => sum + (Number(r.total) || 0), 0);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const pagedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleExportToExcel = async () => {
    try {
      setIsExporting(true);

      // Prepare the data for export
      const exportData = rows.map((r, index) => ({
        'S.No': index + 1,
        'Order #': r.orderNumber || '–',
        'Date': r.orderDate ? format(new Date(r.orderDate), 'dd/MM/yyyy') : '–',
        'Customer': r.customerName || '–',
        'Salesman': r.salesmanName ? `${r.salesmanName}${r.salesmanPhone ? ` (${r.salesmanPhone})` : ''}` : '–',
        'Location': r.locationName || '–',
        'Total': r.total || 0
      }));

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(exportData);

      // Set column widths
      const wscols = [
        { wch: 8 },  // S.No
        { wch: 15 }, // Order #
        { wch: 15 }, // Date
        { wch: 30 }, // Customer
        { wch: 30 }, // Salesman
        { wch: 20 }, // Location
        { wch: 15 }  // Total
      ];
      ws['!cols'] = wscols;

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Add report header info
      XLSX.utils.sheet_add_aoa(ws, [
        ['Salesman Report'],
        ['Generated on: ' + formatDateTime(new Date())],
        ['Period: ' + format(new Date(fromDate), 'dd/MM/yyyy') + ' to ' + format(new Date(toDate), 'dd/MM/yyyy')],
        [''] // Empty row for spacing
      ], { origin: 'A1' });

      // Add the worksheet to the workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Salesman Report');

      // Generate Excel file
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const dataBlob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });

      // Create download link
      const url = window.URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `salesman_report_${format(new Date(), 'yyyy-MM-dd')}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Report exported successfully');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <Layout>
      <Box component="main" sx={{ flexGrow: 1, py: 2, backgroundColor: 'background.default', minHeight: '100vh' }}>
        <Container maxWidth="xl" sx={reportPageSx.container}>
          <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={reportPageSx.breadcrumbs}>
            <Link component={RouterLink} to="/dashboard" color="inherit" underline="hover">
              Dashboard
            </Link>
            <Link component={RouterLink} to="/reports" color="inherit" underline="hover">
              Reports
            </Link>
            <Typography color="text.primary">Salesman Report</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5, ...reportPageSx.pageHeader }}>
            <Box>
              <Typography variant="h4" sx={{ ...reportPageSx.pageTitle, display: 'flex', alignItems: 'center', gap: 1, m: 0 }}>
                <PersonIcon color="primary" />
                Salesman Report
              </Typography>
            
            </Box>
            <Button
              variant="contained"
              size="small"
              onClick={handleExportToExcel}
              disabled={isExporting || rows.length === 0}
              startIcon={isExporting ? <CircularProgress size={18} color="inherit" /> : <FileDownloadIcon />}
            >
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
          </Box>

          {/* Filters */}
          <Card sx={reportPageSx.filterCard}>
            <CardContent sx={reportPageSx.filterCardContent}>
              <Grid
                container
                spacing={reportPageSx.filterGrid.spacing}
                alignItems="flex-end"
                sx={{ flexWrap: { xs: 'wrap', md: 'nowrap' } }}
              >
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                  <TextField
                    fullWidth
                    label="From Date"
                    type="date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                  <TextField
                    fullWidth
                    label="To Date"
                    type="date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0 }}>
                  <TextField
                    fullWidth
                    label="Invoice No"
                    placeholder="e.g. INV-012"
                    value={invoiceNo}
                    onChange={(e) => setInvoiceNo(e.target.value)}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Salesman</InputLabel>
                    <Select
                      value={salesmanId}
                      label="Salesman"
                      onChange={(e) => setSalesmanId(e.target.value || '')}
                    >
                      <MenuItem value="">All</MenuItem>
                      {salesmen.map((sm) => (
                        <MenuItem key={sm.id} value={sm.id}>
                          {sm.name} {sm.phone ? `(${sm.phone})` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} md={2} sx={{ minWidth: 0, flexShrink: 0 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={fetchReport}
                    disabled={loading}
                    sx={{ ...reportPageSx.filterButton, whiteSpace: 'nowrap' }}
                  >
                    {loading ? <CircularProgress size={20} color="inherit" /> : 'Apply'}
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
              <CircularProgress size={32} />
            </Box>
          ) : (
            <>
              <Grid container spacing={reportPageSx.metricsGrid.spacing} sx={reportPageSx.metricsGrid}>
                <Grid item xs={12} sm={6}>
                  <ReportMetricCard
                    label="Total Bills"
                    value={totalBills}
                    icon={<ReceiptIcon />}
                    accentColor="primary.main"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ReportMetricCard
                    label="Total Amount"
                    value={formatCurrency(totalAmount)}
                    icon={<CurrencyRupeeIcon />}
                    accentColor="success.main"
                    valueColor="success.main"
                  />
                </Grid>
              </Grid>

              <Card sx={reportPageSx.sectionCard}>
                <CardContent sx={{ ...reportPageSx.sectionCardContent, pt: 1.5 }}>
                  <Typography sx={reportPageSx.sectionTitle}>Salesman bills</Typography>
                <TableContainer component={Paper} elevation={0} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'action.hover' }}>
                        <TableCell><strong>Order #</strong></TableCell>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Customer</strong></TableCell>
                        <TableCell><strong>Salesman</strong></TableCell>
                        <TableCell><strong>Location</strong></TableCell>
                        <TableCell align="right"><strong>Total</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {rows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={6} align="center">
                            No bills in this range.
                          </TableCell>
                        </TableRow>
                      ) : (
                        pagedRows.map((r) => (
                          <TableRow key={r.orderId} hover>
                            <TableCell>{r.orderNumber || '–'}</TableCell>
                            <TableCell>
                              {r.orderDate ? format(new Date(r.orderDate), 'dd/MM/yyyy') : '–'}
                            </TableCell>
                            <TableCell>{r.customerName || '–'}</TableCell>
                            <TableCell>
                              {r.salesmanName
                                ? `${r.salesmanName}${r.salesmanPhone ? ` (${r.salesmanPhone})` : ''}`
                                : '–'}
                            </TableCell>
                            <TableCell>{r.locationName || '–'}</TableCell>
                            <TableCell align="right">{formatCurrency(r.total)}</TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  rowsPerPageOptions={[10]}
                  component="div"
                  count={rows.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  size="small"
                />
                </CardContent>
              </Card>
            </>
          )}
        </Container>
      </Box>
    </Layout>
  );
};

export default SalesmanReportPage;
